import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Send } from 'lucide-react'

export function ContactsPage() {
  const { t } = useTranslation('common')
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000))

    setIsSubmitting(false)
    setSubmitStatus('success')
    setFormState({ name: '', email: '', subject: '', message: '' })

    // Reset status after 5 seconds
    setTimeout(() => setSubmitStatus('idle'), 5000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const contactInfo = [
    {
      icon: MapPin,
      label: t('contacts.info.address'),
      value: `${t('contact.address')}, ${t('contact.city')}`,
      href: 'https://maps.google.com/?q=Via+Vigone+51+Villafranca+Piemonte',
    },
    {
      icon: Phone,
      label: t('contacts.info.phone'),
      value: t('contact.phone'),
      href: `tel:${t('contact.phone')}`,
    },
    {
      icon: Mail,
      label: t('contacts.info.email'),
      value: t('contact.email'),
      href: `mailto:${t('contact.email')}`,
    },
    {
      icon: Clock,
      label: t('contacts.info.hours'),
      value: t('contacts.info.hoursValue'),
    },
  ]

  return (
    <>
      {/* Contact Info + Form Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-8">
                {t('contacts.info.title')}
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-brand" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500 mb-1">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('http') ? '_blank' : undefined}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-lg text-neutral-900 hover:text-brand transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-lg text-neutral-900">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-xl font-heading font-semibold text-neutral-900 mb-4">
                  {t('contacts.social.title')}
                </h3>
                <p className="text-neutral-600 mb-4">{t('contacts.social.description')}</p>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/Bonifanti1932/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-brand hover:text-neutral-900 transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.instagram.com/bonifanti_panettone/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-brand hover:text-neutral-900 transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-neutral-50 p-8 rounded-lg">
              <h2 className="text-2xl font-heading font-bold text-neutral-900 mb-2">
                {t('contacts.form.title')}
              </h2>
              <p className="text-neutral-600 mb-6">{t('contacts.form.subtitle')}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1">
                    {t('contacts.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-brand focus:border-brand outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                    {t('contacts.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-brand focus:border-brand outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-neutral-700 mb-1">
                    {t('contacts.form.subject')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-brand focus:border-brand outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">
                    {t('contacts.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formState.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-brand text-neutral-900 font-heading font-medium rounded-md hover:bg-brand-dark hover:text-neutral-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{t('contacts.form.send')}</span>
                  <Send className="w-4 h-4" />
                </button>

                {submitStatus === 'success' && (
                  <p className="text-green-600 text-center">{t('contacts.form.success')}</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-600 text-center">{t('contacts.form.error')}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-neutral-100">
        <iframe
          src="https://maps.google.com/maps?q=Bonifanti+SpA,+Via+Vigone+51,+10068+Villafranca+Piemonte+TO,+Italy&t=&z=15&ie=UTF8&iwloc=&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Bonifanti SpA - Via Vigone, 51 - Villafranca Piemonte"
        />
      </section>
    </>
  )
}
